import { createApi } from "@reduxjs/toolkit/query/react";

import { baseQueryWithReauth } from "../baseQueryWithReauth";
import { PRACTICE_ID_HEADER_NAME } from "../constants/api.constant";
import { OAuthGrantType } from "../enums/EOAuthGrantType.enum";
import { ITokenResponse } from "../interfaces/ITokenResponse.interface";

type RefreshTokenParams = {
  readonly refresh_token: string;
};

type LogoutParams = {
  readonly accessToken: string;
};

type CredentialsTokenParamsBase = {
  grant_type: OAuthGrantType;
  practiceId: string;
  patientId: string;
  formAssignmentNotificationId?: string;
  formAssignmentId: string;
  serverSignature: string;
};

export type PatientCredentialsTokenParams = CredentialsTokenParamsBase & {
  grant_type: OAuthGrantType.PatientCredentials;
  serverSignature: string;
  dateOfBirth: string;
};

export type DeviceCredentialsTokenParams = CredentialsTokenParamsBase & {
  grant_type: OAuthGrantType.DeviceCredentials;
  deviceSecret: string;
  deviceId: string;
};

export type QRCodeTokenCredentialsTokenParams = CredentialsTokenParamsBase & {
  grant_type: OAuthGrantType.QRCodeCredentials;
  qrCodeId: string;
  qrCodeSecret: string;
};

export type DottLinkTokenCredentialsTokenParams = CredentialsTokenParamsBase & {
  grant_type: OAuthGrantType.DottLinkCredentials;
  dottLinkId: string;
  dottLinkSecret: string;
};

type CredentialsTokenParams =
  | PatientCredentialsTokenParams
  | DeviceCredentialsTokenParams
  | QRCodeTokenCredentialsTokenParams
  | DottLinkTokenCredentialsTokenParams;

export const oauthApi = createApi({
  reducerPath: "oauthApi",
  baseQuery: baseQueryWithReauth(
    `${process.env.REACT_APP_API_HOST}api/v1/oauth`
  ),
  endpoints: (build) => ({
    tokenCredentials: build.mutation<ITokenResponse, CredentialsTokenParams>({
      query: (params) => ({
        url: "token",
        method: "POST",
        body: JSON.stringify(params),
        headers: [[PRACTICE_ID_HEADER_NAME, params.practiceId]],
      }),
    }),
    refreshToken: build.mutation<ITokenResponse, RefreshTokenParams>({
      query: (params) => ({
        url: "token",
        method: "POST",
        body: JSON.stringify({
          grant_type: OAuthGrantType.RefreshToken,
          ...params,
        }),
      }),
    }),
    logout: build.mutation<void, LogoutParams>({
      query: () => ({
        url: "logout",
        method: "POST",
      }),
    }),
  }),
});

export const {
  useRefreshTokenMutation,
  useLogoutMutation,
  useTokenCredentialsMutation,
} = oauthApi;
